// ═══════════════════════════════════════════════════
//  Button hardware, resolved in exactly one place
//
//  There are eight button-hardware blocks in this repo — four server cost
//  helpers and four client ones — and they are supposed to be numeric mirrors.
//  They have drifted twice: the banquette base never got a hardware map, and
//  the settings default landed on the server only. Both drifts priced a
//  component cheap with no error anywhere.
//
//  This function is the whole rule. Call sites decide only where to put the
//  cost and the warning, never what either one is.
// ═══════════════════════════════════════════════════

/** What costing needs off one Hardware Library row. */
export interface ButtonHardwareEntry {
  cost_per_unit: number;
  name: string;
  unit?: string;
}

export interface ButtonHardwareResolution {
  /** The id actually used: the design's own, else the settings default, else null. */
  hardware_id: string | null;
  /** Null when nothing was found. Callers push a hardware_items row only when
   *  this is non-null — a row cannot be labelled without it. */
  name: string | null;
  cost_per_unit: number;
  cost: number;
  /** Non-null when buttons exist but could not be charged at a real rate. */
  warning: string | null;
}

const NOTHING: ButtonHardwareResolution = {
  hardware_id: null,
  name: null,
  cost_per_unit: 0,
  cost: 0,
  warning: null,
};

/**
 * Resolve and price the button hardware for one component.
 *
 * `lookup` is a callback rather than a Map because the two sides source their
 * rates differently: the server's map is already IVA- and currency-converted by
 * the service's buildHardwareMap, while the cushion clients convert inline
 * through their own hwConversions map. Handing this function a Map would force
 * one of them to convert twice or not at all.
 *
 * @param buttonCount   from resolveButtonCount — already gated on the design's
 *                      has_buttons trait. Zero returns silently: the default
 *                      supplies hardware for buttons that exist, it never
 *                      creates them.
 */
export function resolveButtonHardware(
  buttonCount: number,
  designButtonHardwareId: string | null | undefined,
  defaultButtonHardwareId: string | null | undefined,
  lookup: (id: string) => ButtonHardwareEntry | undefined,
): ButtonHardwareResolution {
  if (!(buttonCount > 0)) return { ...NOTHING };

  // `||`, not `??`: a stored empty string is not a real id and must fall
  // through to the default exactly like null/undefined do. Not reachable
  // today — every design selector normalizes with `(key as string) || null`
  // before this runs — but a `??` here would silently misreport a configured
  // default as absent, which is the exact class of misleading silence this
  // function exists to remove.
  const id = designButtonHardwareId || defaultButtonHardwareId || null;
  if (!id) {
    return {
      ...NOTHING,
      warning:
        `${buttonCount} buttons priced at $0.00 — this design names no button ` +
        `hardware and no default is set in Configuration > Upholstery.`,
    };
  }

  const entry = lookup(id);
  if (!entry) {
    return {
      ...NOTHING,
      hardware_id: id,
      warning:
        `${buttonCount} buttons priced at $0.00 — button hardware ${id} was ` +
        `not found in the Hardware Library.`,
    };
  }

  const rate = Number.isFinite(entry.cost_per_unit) ? entry.cost_per_unit : 0;
  if (!(rate > 0)) {
    // Still named, so the caller pushes a visible $0.00 row — which is what it
    // did before this function existed. The warning is the new part.
    return {
      hardware_id: id,
      name: entry.name,
      cost_per_unit: 0,
      cost: 0,
      warning:
        `${buttonCount} buttons priced at $0.00 — "${entry.name}" has no cost ` +
        `per unit in the Hardware Library.`,
    };
  }

  return {
    hardware_id: id,
    name: entry.name,
    cost_per_unit: rate,
    cost: buttonCount * rate,
    warning: null,
  };
}
